import { motion } from 'framer-motion';
import React, { FC, useState, useContext } from 'react';
import YouTube, { Options } from 'react-youtube';
import useNextImageLoader from '../../../hooks/use-next-image-loader/use-next-image-loader';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import { InlineVideoType } from '../../../models/inline-video-type';
import IcomoonIconButton from '../../atoms/icomoon-icon-button/icomoon-icon-button';
import Image from '../../atoms/image/image';
import OverlayContainer from '../../atoms/overlay-container/overlay-container';

const InlineImageVideo: FC<InlineVideoType> = ({
  caption,
  imageAltText,
  image,
  youTubeVideoEmbedCode,
}: InlineVideoType) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const [playAnimation, setPlayAnimation] = useState<boolean>(false);
  const [playClicked, setPlayClicked] = useState<boolean>(false);
  const [player, setPlayer] = useState(null);

  const opts = {
    playerVars: {
      autoplay: 0,
    },
  };
  const hasImage = !!image?.desktopUrl;

  const onReady = (event) => {
    setPlayer(event.target);
  };

  const handlePlayClick = () => {
    setPlayAnimation(true);

    player?.playVideo();

    setTimeout(() => {
      setPlayClicked(true);
    }, 600);
  };

  const imageVariants = {
    playClicked: { borderRadius: '0rem', opacity: 0 },
    playNotClicked: { borderRadius: '0.75rem', opacity: 1 },
  };

  const videoVariants = {
    playClicked: { borderRadius: '0rem' },
    playNotClicked: { borderRadius: hasImage ? '0.75rem' : '0rem' },
  };

  const transition = {
    duration: 0.6,
  };

  return (
    <figure className="relative w-full">
      {hasImage && (
        <motion.div
          animate={playAnimation ? 'playClicked' : 'playNotClicked'}
          className={
            `${playClicked ? 'hidden' : 'flex'} ` +
            'items-center justify-center overflow-hidden relative z-20'
          }
          transition={transition}
          variants={imageVariants}
        >
          <OverlayContainer />

          <div className="absolute border-3 border-white rounded-full z-30">
            <IcomoonIconButton
              className="p-8"
              color="white"
              name="play"
              onClick={() => handlePlayClick()}
              size="lg"
            />
          </div>

          <Image
            alt={imageAltText}
            loader={useNextImageLoader}
            model={image}
            priority
          />
        </motion.div>
      )}

      <motion.div
        animate={playAnimation ? 'playClicked' : 'playNotClicked'}
        className={
          `${playClicked || !hasImage ? 'relative' : 'absolute top-0'} ` +
          'overflow-hidden pb-ratio-16-9 w-full z-10'
        }
        transition={transition}
        variants={videoVariants}
      >
        <YouTube
          className="absolute h-full w-full"
          videoId={youTubeVideoEmbedCode}
          opts={opts as Options}
          onReady={onReady}
          onPlay={() => {
            pushToDataLayer(GtmEventNameEnum.GtmVideo);
          }}
          onPause={() => {
            pushToDataLayer(GtmEventNameEnum.GtmVideo);
          }}
        />
      </motion.div>

      {caption && (
        <figcaption className="mt-2 text-sm text-grey-600">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

InlineImageVideo.defaultProps = {
  caption: null,
};

export default InlineImageVideo;
